import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faWallet } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import PaymentLoading from '../components/PaymentLoading';
import './AddFund.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const POLL_INTERVAL_MS = 3000;
const POLL_DURATION_MS = 120000;
const terminalStatuses = new Set(['completed', 'failed']);
const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export default function AddFund() {
  const { token } = useAuth();
  const mounted = useRef(true);
  const [mobileMethods, setMobileMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [minDeposit, setMinDeposit] = useState(1000);
  const [txns, setTxns] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingId, setCheckingId] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const replaceTransaction = useCallback((updated) => {
    setTxns((current) => current.map((transaction) => transaction.id === updated.id ? updated : transaction));
  }, []);

  useEffect(() => {
    mounted.current = true;
    Promise.all([
      fetch(`${API}/payment-methods`).then((response) => response.json()),
      fetch(`${API}/settings/min-deposit`).then((response) => response.json()),
      fetch(`${API}/transactions`, { headers }).then((response) => response.json()),
    ]).then(([methods, settings, transactions]) => {
      if (!mounted.current) return;
      setMobileMethods(Array.isArray(methods) ? methods : []);
      setMinDeposit(Number(settings.min_deposit) || 1000);
      setTxns(Array.isArray(transactions) ? transactions : []);
    }).catch(() => setError('Some payment information could not be loaded.'));

    return () => { mounted.current = false; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkStatus = useCallback(async (transaction, manual = false) => {
    if (manual) setCheckingId(transaction.id);
    try {
      const response = await fetch(`${API}/transactions/${transaction.id}/status`, { method: 'POST', headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Status check failed.');
      if (mounted.current) replaceTransaction(data);
      return data;
    } catch (statusError) {
      if (manual && mounted.current) setError(statusError.message);
      return transaction;
    } finally {
      if (manual && mounted.current) setCheckingId(null);
    }
  }, [token, replaceTransaction]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollPayment = async (transaction) => {
    const deadline = Date.now() + POLL_DURATION_MS;
    let current = transaction;
    while (mounted.current && Date.now() < deadline && !terminalStatuses.has(current.status)) {
      await delay(POLL_INTERVAL_MS);
      if (!mounted.current) return;
      current = await checkStatus(current);
    }

    if (mounted.current && !terminalStatuses.has(current.status)) {
      const response = await fetch(`${API}/transactions/${current.id}/timeout`, { method: 'POST', headers });
      const timedOut = await response.json();
      if (response.ok) replaceTransaction(timedOut);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!selectedMethod) return setError('Select a payment method.');
    if (!phone || phone.length < 10) return setError('Enter a valid Tanzanian phone number.');
    if (!amount || Number(amount) < minDeposit) return setError(`Minimum deposit is TSHS ${Number(minDeposit).toLocaleString()}.`);

    setLoading(true);
    try {
      const response = await fetch(`${API}/transactions`, {
        method: 'POST', headers,
        body: JSON.stringify({ method: selectedMethod.id, phone, amount: Number(amount) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Payment request failed.');
      setTxns((current) => [data, ...current]);
      setPhone('');
      setAmount('');
      setSelectedMethod(null);
      await pollPayment(data);
    } catch (paymentError) {
      if (mounted.current) setError(paymentError.message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const statusClass = (status) => status === 'completed'
    ? 'status-completed'
    : (status === 'failed' || status === 'timed_out' ? 'status-cancelled' : 'status-pending');

  return (
    <div className="add-fund">
      <div className="fund-grid">
        <div className="fund-form-section">
          <h2 className="section-title">Mobile Money</h2>
          <p className="fund-hint">Minimum deposit: TSHS {Number(minDeposit).toLocaleString()}</p>
          {mobileMethods.length === 0 && <div className="auth-error">Deposits are currently unavailable. Please try again later.</div>}
          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="payment-methods">
              {mobileMethods.map((method) => (
                <button type="button" key={method.id} className={`payment-method ${selectedMethod?.id === method.id ? 'selected' : ''}`} onClick={() => setSelectedMethod(method)}>
                  <img src={method.logo} alt="" className="method-logo" />
                  <span className="method-name">{method.name}</span>
                </button>
              ))}
            </div>
            <div className="amount-section">
              <div className="form-group">
                <label className="form-label" htmlFor="deposit-phone">Phone Number</label>
                <input id="deposit-phone" type="tel" className="form-input" placeholder="07XXXXXXXX" value={phone} onChange={(event) => setPhone(event.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="deposit-amount">Amount (TSHS)</label>
                <div className="amount-input-group">
                  <span className="currency-symbol">TSHS</span>
                  <input id="deposit-amount" type="number" className="amount-input" placeholder={String(minDeposit)} min={minDeposit} value={amount} onChange={(event) => setAmount(event.target.value)} required />
                </div>
              </div>
              <button type="submit" className="submit-btn" disabled={loading || mobileMethods.length === 0}>
                {loading ? 'Waiting for confirmation...' : `Deposit TSHS ${amount ? Number(amount).toLocaleString() : '0'}`}
              </button>
            </div>
          </form>
        </div>

        <div className="fund-history-section">
          <h2 className="section-title"><FontAwesomeIcon icon={faWallet} /> Payment History</h2>
          <div className="history-list">
            {txns.length === 0 && <p className="fund-empty">No transactions yet.</p>}
            {txns.map((transaction) => (
              <div key={transaction.id} className="history-item">
                <div className="history-left">
                  <span className="history-method">{transaction.method} · {transaction.gateway || 'payment'}</span>
                  <span className="history-id">{transaction.reference || transaction.phone}</span>
                </div>
                <div className="history-right">
                  <span className="history-amount">TSHS {Number(transaction.amount).toLocaleString()}</span>
                  <span className={`status-badge ${statusClass(transaction.status)}`}>{transaction.status.replace('_', ' ')}</span>
                  {transaction.reference && transaction.status !== 'completed' && transaction.status !== 'failed' && (
                    <button className="status-check-btn" type="button" disabled={checkingId === transaction.id} onClick={() => checkStatus(transaction, true)} aria-label="Check payment status">
                      <FontAwesomeIcon icon={faRotate} spin={checkingId === transaction.id} /> Check
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PaymentLoading show={loading} message="Thibitisha malipo kwenye simu yako kuendelea na huduma zetu!" />
    </div>
  );
}
